import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, LoaderIcon, TruckIcon } from 'lucide-react';
import { builderPrices } from '../../data/builder';
import { BuilderApi } from '../../hooks/useBuilder';
import { protectionLabel } from '../../utils/builderCart';
import { money } from '../QuantityStepper';
import { SecureCheckoutNote } from '../SecureCheckoutNote';
interface BuilderSummaryProps {
  builder: BuilderApi;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function NotConnected({
  builder
}: BuilderSummaryProps) {
  if (!builder.notConnected) return null;
  return <div role="alert" className="border border-white/20 bg-white/[0.04] px-3 py-2.5 text-[13px] text-steel-200" data-id="element-184" data-mp="3f01qk3zqfdgw">
      {builder.state.complete ? <>Part of this build isn&apos;t connected to checkout yet. Please try again shortly or email support@gritvuegear.com.</> : <>
          Essential checkout opens shortly. Want your X1 today?{' '}
          <button type="button" onClick={builder.upgradeToComplete} className="font-bold text-grit-500 underline underline-offset-2 hover:text-grit-400" data-id="element-185" data-mp="3kst8omon5bvr">
            Switch to the Complete System — {money(builderPrices.complete)}
          </button>
        </>}
    </div>;
}
function CheckoutButton({
  builder,
  compact = false,
  onClick
}: BuilderSummaryProps & {
  compact?: boolean;
  onClick?: () => void;
}) {
  return <button type="button" onClick={onClick ?? builder.checkout} disabled={builder.routing} className={`inline-flex items-center justify-center gap-2 bg-grit-500 font-display font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite ${compact ? 'shrink-0 px-4 py-3.5 text-base' : 'w-full px-5 py-4 text-xl'}`} data-id="element-186" data-mp="2x2p5ggfd7e5a">
      {builder.routing ? 'Opening cart' : 'Continue'}
      {builder.routing ? <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-187" data-mp="28ivimbfj6zgi" /> : <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-188" data-mp="1uiku3f1p1wyi" />}
    </button>;
}

/** Desktop: persistent panel. Mirrors every selection and price in real time. */
export function BuilderSummary({
  builder
}: BuilderSummaryProps) {
  const {
    lines,
    total,
    state
  } = builder;
  return <aside aria-label="Your build" className="border border-white/10 bg-ink-900 p-5" data-id="element-189" data-mp="2r8tqaqff2qjm">
      <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-steel-400" data-id="element-190" data-mp="2dh5eko92y436">Your X1</p>
      <ul className="mt-3 grid gap-2" aria-live="polite" data-id="element-191" data-mp="2ig04ea5gssws">
        <AnimatePresence initial={false} data-id="element-192" data-mp="2ief9gjf8n7ye">
          {lines.map(line => <motion.li key={line.label} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.18,
          ease: EASE
        }} className="flex items-start justify-between gap-3 overflow-hidden text-[14px]" data-id="element-193" data-mp="364jd2qsff7pe">
              <span className="flex items-start gap-2 text-offwhite" data-id="element-194" data-mp="3u1fpq387rqlo">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-195" data-mp="3u2quo6deb4ed" />
                {line.label}
                {line.quantity > 1 ? ` × ${line.quantity}` : ''}
              </span>
              <span className="shrink-0 tabular-nums text-steel-300" data-id="element-196" data-mp="2xanaf7t3u2ky">{money(line.price * line.quantity)}</span>
            </motion.li>)}
        </AnimatePresence>
        <li className="flex items-start justify-between gap-3 text-[13px] text-steel-400" data-id="element-197" data-mp="3bbptrcspsys6">
          <span data-id="element-198" data-mp="36a0j3y9emn4m">{protectionLabel(state)}</span>
        </li>
      </ul>
      <div className="mt-4 flex items-end justify-between border-t border-white/10 pt-4" data-id="element-199" data-mp="qj4khlfdh6fs">
        <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-steel-400" data-id="element-200" data-mp="llw15hihmwcy">Total</span>
        <span className="font-display text-4xl font-extrabold leading-none tabular-nums text-offwhite" data-id="element-201" data-mp="2ra6isr3v24re">{money(total)}</span>
      </div>
      <p className="mt-2 flex items-center gap-2 text-[13px] font-semibold text-grit-500" data-id="element-202" data-mp="2w8q5itqa3kkt">
        <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-203" data-mp="315zs81ma9i4y" />
        Free U.S. shipping
      </p>
      <div className="mt-4 grid gap-3" data-id="element-204" data-mp="35apmxn57gqoa">
        <CheckoutButton builder={builder} data-id="element-205" data-mp="3a88ygfaiqpcj" />
        <NotConnected builder={builder} data-id="element-206" data-mp="3a7g1g61d3e7l" />
      </div>
      <SecureCheckoutNote className="mt-4" data-id="element-207" data-mp="3f7c4o328j1m3" />
    </aside>;
}

/** Mobile: compact sticky bar, visible only while the builder is on screen. */
export function BuilderSummaryBar({
  builder,
  visible
}: BuilderSummaryProps & {
  visible: boolean;
}) {
  const {
    lines,
    total,
    state
  } = builder;
  const addOns = lines.length - 1;
  return <AnimatePresence data-id="element-208" data-mp="3szid12z5c4az">
      {visible && <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      duration: 0.22,
      ease: EASE
    }} className="fixed inset-x-0 bottom-0 z-40 border-t border-grit-500/40 bg-ink-950/97 px-4 py-3 backdrop-blur lg:hidden" data-id="element-209" data-mp="3swnr170hibfg">
          {builder.notConnected && <div className="mb-2" data-id="element-210" data-mp="1d90auqjjeb0z">
              <NotConnected builder={builder} data-id="element-211" data-mp="1dabn7er1sr3p" />
            </div>}
          <div className="flex items-center justify-between gap-3" data-id="element-212" data-mp="18bqnrhjmebgb">
            <div className="min-w-0" data-id="element-213" data-mp="250px6zvxxja3">
              <p className="truncate text-[11px] font-bold uppercase tracking-wide text-steel-400" data-id="element-214" data-mp="24zoc02l7ngen">
                {state.complete ? 'Complete System' : 'X1 Essential'}
                {addOns > 0 ? ` · ${addOns} add-on${addOns > 1 ? 's' : ''}` : ''} · Free U.S. shipping
              </p>
              <p className="font-display text-2xl font-extrabold leading-none tabular-nums text-offwhite" data-id="element-215" data-mp="1r8i71qz5wqkd">
                <span className="text-base text-steel-300" data-id="element-216" data-mp="2opqlcluztp0e">Your X1 — </span>
                {money(total)}
              </p>
            </div>
            <CheckoutButton builder={builder} compact onClick={builder.step === 'review' ? builder.checkout : () => {
          builder.goToStep('review');
          document.getElementById('builder-panel')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }} data-id="element-217" data-mp="2jwe1ep19e6dz" />
          </div>
        </motion.div>}
    </AnimatePresence>;
}